import React from 'react';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import InlineMessage from '@atlaskit/inline-message';
import Button, { LoadingButton } from '@atlaskit/button';
import { CCPCreditCardFormState } from '@atlassian/commerce-ui/credit-card-ccp';
import { BillingProfile, PaymentMethodTypeV2 } from '@css-xp/graphtypes/src/generated-types-new';
import { CreatePaymentMethodContext } from '@css-xp/ppc-paypal';
import { useReplacePaymentMethod } from './hooks/use-replace-payment-method';
import { OnUpdateCallbacks } from './hooks/types';
import { EditPaymentMethodProps, PaymentMethodFormProps, PaymentMethodOperation } from './types';
import { PaymentMethodForm } from './payment-method-form';

const EditPaymentMethodForm: React.FC<
  Omit<PaymentMethodFormProps, 'onSubmit' | 'isOperationInProgress'> & {
    txaId: string;
    paymentMethodId: string;
    version?: number;
    isLoadingBillingProfiles?: boolean;
    billingProfiles?: BillingProfile[];
    isPaypalSupportEnabled?: boolean;
    paypalEmail?: string;
    paymentMethod?: PaymentMethodTypeV2;
  } & OnUpdateCallbacks
> = ({
  paymentMethodId,
  onCancel,
  txaId,
  onUpdateDone,
  onUpdateSuccess,
  onUpdateFailure,
  successMessage,
  failureMessage,
  showSubscriptionChargeNote,
  subHeader,
  version,
  isLoadingBillingProfiles,
  billingProfiles,
  isPaypalSupportEnabled,
  paypalEmail,
  paymentMethod,
}) => {
  const [paypalData, setPaypalData] = React.useState<CreatePaymentMethodContext | null>(null);
  const {
    creationStage,
    generatePaymentMethod,
    updateCard,
    closeWarning,
    isWarningBeingShown,
    openWarning,
    updatePaypal,
    isPaypalOperationInProgress,
  } = useReplacePaymentMethod({
    txaId,
    idOfPaymentMethodBeingReplaced: paymentMethodId,
    onUpdateDone,
    onUpdateSuccess,
    onUpdateFailure,
    successMessage,
    failureMessage,
  });

  const onPaypalSubmit = (
    _: any,
    createPaymentMethodContext: React.SetStateAction<CreatePaymentMethodContext | null>
  ) => {
    setPaypalData(createPaymentMethodContext);
    openWarning();
  };

  const onPaypalWarningConfirm = async () => {
    if (paypalData && updatePaypal) {
      await updatePaypal(txaId, paypalData);
    }
  };

  return (
    <>
      <PaymentMethodForm
        txaId={txaId}
        operation={PaymentMethodOperation.REPLACE}
        onCancel={onCancel}
        isOperationInProgress={creationStage === 'creation-in-progress'}
        onSubmit={generatePaymentMethod}
        showSubscriptionChargeNote={showSubscriptionChargeNote}
        subHeader={subHeader}
        version={version}
        header={version === 2 ? 'Replace card' : undefined}
        isLoadingBillingProfiles={isLoadingBillingProfiles}
        billingProfiles={billingProfiles}
        isPaypalSelectionEnabled={isPaypalSupportEnabled}
        paypalEmail={paypalEmail}
        editPaymentMethod={isPaypalSupportEnabled}
        onPaypalSubmit={onPaypalSubmit}
        isPaypalOperationInProgress={isPaypalOperationInProgress}
        paymentMethod={paymentMethod}
        sidebarClassName="payment-sidebar"
      />
      <ModalTransition>
        {isWarningBeingShown && (
          <Modal onClose={closeWarning} testId="replace-payment-method-modal">
            <ModalHeader>
              <ModalTitle>
                <InlineMessage
                  type="warning"
                  secondaryText={version === 2 ? 'Replace card' : "You're about to update this payment method"}
                />
              </ModalTitle>
            </ModalHeader>
            <ModalBody>
              {version === 2 ? (
                <p>All billing profiles that use this card will also get updated.</p>
              ) : (
                <p>
                  If you confirm, all subscriptions that use this payment method will also get updated. We'll charge the
                  updated payment method for those subscriptions.
                </p>
              )}
            </ModalBody>
            <ModalFooter>
              <div className="replace-payment-method-footer">
                <Button appearance="subtle" onClick={closeWarning}>
                  Cancel
                </Button>
                <LoadingButton
                  appearance="warning"
                  isLoading={
                    creationStage === 'confirmation' || (isPaypalSupportEnabled && isPaypalOperationInProgress)
                  }
                  onClick={
                    isPaypalSupportEnabled && paymentMethod === PaymentMethodTypeV2.PAYPAL
                      ? onPaypalWarningConfirm
                      : updateCard
                  }
                  autoFocus
                  testId="replace-payment-method-btn"
                >
                  Confirm
                </LoadingButton>
              </div>
            </ModalFooter>
          </Modal>
        )}
      </ModalTransition>
    </>
  );
};

export const EditPaymentMethod: React.FC<EditPaymentMethodProps> = ({
  txaId,
  onCancel,
  paymentMethodId,
  onUpdateDone,
  onUpdateFailure,
  onUpdateSuccess,
  successMessage,
  failureMessage,
  version,
  ...otherProps
}) => {
  return (
    <CCPCreditCardFormState accountId={txaId}>
      <EditPaymentMethodForm
        txaId={txaId}
        operation={PaymentMethodOperation.EDIT}
        onCancel={onCancel}
        paymentMethodId={paymentMethodId}
        onUpdateDone={onUpdateDone}
        onUpdateSuccess={onUpdateSuccess}
        onUpdateFailure={onUpdateFailure}
        successMessage={successMessage}
        failureMessage={failureMessage}
        version={version}
        {...otherProps}
      />
    </CCPCreditCardFormState>
  );
};
